import React, { useEffect, useState } from "react";
import AuthService from "../service/AmplifyAuth";
import StringUtils from "../utils/StringUtils";
import EmailUtils from "../utils/EmailUtils";
import ErrorUtils from "../utils/ErrorUtils";
import ChangePassword from "./ChangePassword";
import { Button, Card, Field } from "../components/UI";
import { useAppContext } from "../context/AppContext";
import { getStripeUserByEmail } from "../hooks";
import { useNavigate } from "../router";
import trustdLogo from "../assets/image.webp";

const STAGE_LOGIN = "LOGIN";
const STAGE_FORGOT_PASSWORD = "FORGOT_PASSWORD";
const STAGE_FORGOT_PASSWORD_CODE = "FORGOT_PASSWORD_CODE";
const STAGE_COMPLETE_NEW_PASSWORD_REQUIRED = "COMPLETE_NEW_PASSWORD_REQUIRED";
const STAGE_EMAIL_VERIFICATION = "STAGE_EMAIL_VERIFICATION";
const STAGE_MIGRATED_EMAIL_VERIFICATION = "MIGRATED_EMAIL_VERIFICATION";
const STAGE_MIGRATED_SET_PASSWORD = "MIGRATED_SET_PASSWORD";

function shouldCompleteOnboardingInAccount(detailedUser) {
  const rawValue = detailedUser?.isPopupClosed;
  if (rawValue === false || rawValue === 0) return true;
  if (typeof rawValue === "string") {
    const normalized = rawValue.trim().toLowerCase();
    return normalized === "false" || normalized === "0";
  }
  return false;
}

export default function HostLogin() {
  const [currentStage, setCurrentStage] = useState(STAGE_LOGIN);
  const [verificationCode, setVerificationCode] = useState("");
  const [passwordUser, setPasswordUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ username: "", password: "", code: "", newPassword1: "", newPassword2: "" });
  const [fieldErrors, setFieldErrors] = useState({});
  const [isBusy, setIsBusy] = useState(false);
  const { commit, addToast } = useAppContext();
  const navigate = useNavigate();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("is_api") && urlParams.get("is_api") === "ownerrez") {
      addToast({
        severity: "success",
        summary: "Success",
        detail: "Your Ownerrez account has been successfully linked to your account!",
        life: 10000,
      });
    }
  }, [addToast]);

  const clearFieldError = (field, value, nextForm = loginForm) => {
    setFieldErrors((errors) => {
      if (!errors[field]) return errors;
      const stringValue = String(value || "").trim();
      let shouldClear = !StringUtils.isEmpty(value);

      if (field === "username") shouldClear = EmailUtils.isValid(value);
      if (field === "newPassword1" || field === "newPassword2") {
        const password = field === "newPassword1" ? value : nextForm.newPassword1;
        const confirmation = field === "newPassword2" ? value : nextForm.newPassword2;
        shouldClear = Boolean(password && confirmation && password === confirmation);
      }
      if (field === "verificationCode" || field === "code") shouldClear = Boolean(stringValue);

      if (!shouldClear) return errors;
      const nextErrors = { ...errors };
      delete nextErrors[field];
      if (field === "newPassword1" || field === "newPassword2") {
        delete nextErrors.newPassword1;
        delete nextErrors.newPassword2;
      }
      return nextErrors;
    });
  };

  const updateField = (field, value) => {
    setLoginForm((form) => {
      const nextForm = { ...form, [field]: value };
      clearFieldError(field, value, nextForm);
      return nextForm;
    });
  };

  const updateVerificationCode = (value) => {
    setVerificationCode(value);
    clearFieldError("verificationCode", value);
  };

  const validateLogin = () => {
    const errors = {};
    if (StringUtils.isEmpty(loginForm.username)) {
      addToast({ severity: "error", summary: "Validation Error", detail: "Email is required", life: 5000 });
      errors.username = "Email is required";
    } else if (!EmailUtils.isValid(loginForm.username)) {
      addToast({ severity: "error", summary: "Validation Error", detail: "Email is not a valid email address", life: 5000 });
      errors.username = "Email is not a valid email address";
    }
    if (StringUtils.isEmpty(loginForm.password)) {
      addToast({ severity: "error", summary: "Validation Error", detail: "Password is required", life: 5000 });
      errors.password = "Password is required";
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const login = async () => {
    if (isBusy) return;
    if (!validateLogin()) return;

    try {
      setIsBusy(true);
      const user = await AuthService.signIn(loginForm.username, loginForm.password);
      if (
        user.challengeName === "MIGRATED_ACCOUNT_VERIFICATION_REQUIRED" ||
        user.challengeName === "RESET_PASSWORD_REQUIRED"
      ) {
        addToast({
          severity: "info",
          summary: "Email Verification Required",
          detail: "We sent a one-time verification code to your email address.",
          life: 10000,
        });
        setVerificationCode("");
        setLoginForm((form) => ({
          ...form,
          code: "",
          newPassword1: "",
          newPassword2: "",
        }));
        setFieldErrors({});
        setCurrentStage(STAGE_MIGRATED_EMAIL_VERIFICATION);
      } else if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
        addToast({
          severity: "warn",
          summary: "New Password Required",
          detail: "New Password Required for the first time signing in, please enter your new password!",
          life: 10000,
        });
        setPasswordUser(user);
        setCurrentStage(STAGE_COMPLETE_NEW_PASSWORD_REQUIRED);
      } else {
        commit("signInUser", user);
        const detailedUser = await getStripeUserByEmail(loginForm.username, { forceRefresh: true });
        const target = shouldCompleteOnboardingInAccount(detailedUser) ? "/account" : "/";
        addToast({ severity: "success", summary: "Success", detail: "Login Successful", life: 5000 });
        navigate(target);
      }
    } catch (error) {
      const msg = ErrorUtils.parseError(error);
      addToast({ severity: "error", summary: "Error", detail: msg, life: 10000 });
      if (msg.includes("is not confirmed")) {
        setCurrentStage(STAGE_EMAIL_VERIFICATION);
        const data = await AuthService.sendEmailVerificationCode(loginForm.username);
        if (data?.verificationCode) {
          setVerificationCode(data.verificationCode);
          addToast({
            severity: "info",
            summary: "Dev Verification Code",
            detail: `Use this code: ${data.verificationCode}`,
            life: 10000,
          });
        }
      }
    } finally {
      setIsBusy(false);
    }
  };

  const verifyMigratedEmail = async () => {
    if (isBusy) return;
    if (StringUtils.isEmpty(verificationCode)) {
      setFieldErrors({ verificationCode: "Verification Code is required" });
      return;
    }
    try {
      setIsBusy(true);
      await AuthService.verifyMigratedAccountEmail(loginForm.username, verificationCode);
      setFieldErrors({});
      setCurrentStage(STAGE_MIGRATED_SET_PASSWORD);
      addToast({
        severity: "success",
        summary: "Email Verified",
        detail: "Your email is verified. Create your new portal password to continue.",
        life: 8000,
      });
    } catch (error) {
      addToast({
        severity: "error",
        summary: "Verification Failed",
        detail: ErrorUtils.parseError(error),
        life: 10000,
      });
    } finally {
      setIsBusy(false);
    }
  };

  const completeMigratedAccount = async () => {
    if (isBusy) return;
    const errors = {};
    if (!loginForm.newPassword1) errors.newPassword1 = "New Password is required";
    if (!loginForm.newPassword2) errors.newPassword2 = "Re-Enter New Password is required";
    if (loginForm.newPassword1 && loginForm.newPassword1.length < 8) {
      errors.newPassword1 = "Password must be at least 8 characters";
    }
    if (loginForm.newPassword1 && loginForm.newPassword2 && loginForm.newPassword1 !== loginForm.newPassword2) {
      errors.newPassword2 = "New Password values do not match";
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    try {
      setIsBusy(true);
      const user = await AuthService.completeMigratedAccount(
        loginForm.username,
        verificationCode,
        loginForm.newPassword1,
      );
      commit("signInUser", user);
      const detailedUser = await getStripeUserByEmail(loginForm.username, { forceRefresh: true });
      const target = shouldCompleteOnboardingInAccount(detailedUser) ? "/account" : "/";
      addToast({
        severity: "success",
        summary: "Welcome",
        detail: "Your new portal password is set and your account is ready.",
        life: 8000,
      });
      navigate(target);
    } catch (error) {
      addToast({
        severity: "error",
        summary: "Account Setup Failed",
        detail: ErrorUtils.parseError(error),
        life: 10000,
      });
    } finally {
      setIsBusy(false);
    }
  };

  const verify = async () => {
    if (isBusy) return;
    if (StringUtils.isEmpty(verificationCode)) {
      addToast({ severity: "error", summary: "Validation Error", detail: "Verification Code is required", life: 5000 });
      setFieldErrors({ verificationCode: "Verification Code is required" });
      return;
    }

    try {
      setIsBusy(true);
      await AuthService.confirmSignUp(loginForm.username, verificationCode);
      const user = await AuthService.signIn(loginForm.username, loginForm.password);
      commit("signInUser", user);
      const detailedUser = await getStripeUserByEmail(loginForm.username, { forceRefresh: true });
      const target = shouldCompleteOnboardingInAccount(detailedUser) ? "/account" : "/";
      addToast({ severity: "success", summary: "Success", detail: "Code Verification Successful", life: 5000 });
      navigate(target);
    } catch (error) {
      const msg = ErrorUtils.parseError(error);
      addToast({ severity: "error", summary: "Error", detail: msg, life: 10000 });
    } finally {
      setIsBusy(false);
    }
  };

  const forgotPasswordReset = async () => {
    if (isBusy) return;
    if (StringUtils.isEmpty(loginForm.username)) {
      addToast({ severity: "error", summary: "Validation Error", detail: "Email is required", life: 5000 });
      setFieldErrors({ username: "Email is required" });
      return;
    }
    if (!EmailUtils.isValid(loginForm.username)) {
      addToast({ severity: "error", summary: "Validation Error", detail: "Email is not a valid email address", life: 5000 });
      setFieldErrors({ username: "Email is not a valid email address" });
      return;
    }
    try {
      setIsBusy(true);
      const data = await AuthService.forgotPassword(loginForm.username);
      addToast({ severity: "success", summary: "Success", detail: "Password Reset Code was sent to your email address!", life: 10000 });
      if (data?.verificationCode) {
        updateField("code", data.verificationCode);
        addToast({
          severity: "info",
          summary: "Dev Reset Code",
          detail: `Use this code: ${data.verificationCode}`,
          life: 10000,
        });
      }
      setCurrentStage(STAGE_FORGOT_PASSWORD_CODE);
    } catch (error) {
      const msg = ErrorUtils.parseError(error);
      addToast({ severity: "error", summary: "Error", detail: msg, life: 5000 });
    } finally {
      setIsBusy(false);
    }
  };

  const forgotPasswordCode = async () => {
    if (isBusy) return;
    const errors = {};
    if (!loginForm.code) errors.code = "Password Reset Code is required";
    if (!loginForm.newPassword1) errors.newPassword1 = "New Password is required";
    if (!loginForm.newPassword2) errors.newPassword2 = "Re-Enter New Password is required";
    if (loginForm.newPassword1 && loginForm.newPassword2 && loginForm.newPassword1 !== loginForm.newPassword2) {
      errors.newPassword2 = "New Password values do not match";
    }
    if (Object.keys(errors).length > 0) {
      addToast({ severity: "error", summary: "Validation Error", detail: "Please enter matching new password values and the reset code", life: 5000 });
      setFieldErrors(errors);
      return;
    }
    try {
      setIsBusy(true);
      await AuthService.forgotPasswordSubmit(loginForm.username, loginForm.code, loginForm.newPassword1);
      addToast({ severity: "success", summary: "Success", detail: "Password Changed Successfully!", life: 10000 });
      setCurrentStage(STAGE_LOGIN);
    } catch (error) {
      const msg = ErrorUtils.parseError(error);
      addToast({ severity: "error", summary: "Error", detail: msg, life: 10000 });
    } finally {
      setIsBusy(false);
    }
  };

  const onEnter = (handler) => (event) => {
    if (event.key === "Enter") handler();
  };

  return (
    <div className="loginBody auth-screen auth-modern">
      <header className="auth-modern-topbar">
        <div className="auth-modern-brand">
          <img src={trustdLogo} alt="Trustd.ai" />
          <span>Next Generation Guest Intelligence</span>
        </div>
      </header>
      <main className="auth-modern-main">
        <div className="auth-modern-heading">
          <h1>Welcome to Trustd.ai Host Portal</h1>
          <p>Screen guests, verify identities, and manage your guest pipeline - all in one place.</p>
        </div>
        <div className="auth-modern-card-wrap">
            {currentStage === STAGE_LOGIN ? (
              <Card className="loginCard auth-modern-card auth-modern-card-narrow">
                <Field label="Email address" required className="auth-modern-field" error={fieldErrors.username}>
                  <input type="email" className="form-control" placeholder="you@example.com" value={loginForm.username} autoComplete="username" autoCapitalize="none" spellCheck="false" onKeyDown={onEnter(login)} onChange={(event) => updateField("username", event.target.value)} />
                </Field>
                <Field label="Password" required className="auth-modern-field" error={fieldErrors.password}>
                  <div className="auth-modern-label-action">
                    <a href="#forgot" onClick={(event) => { event.preventDefault(); setCurrentStage(STAGE_FORGOT_PASSWORD); }}>
                      Forgot password?
                    </a>
                  </div>
                  <input type="password" className="form-control" placeholder="Password" value={loginForm.password} autoComplete="current-password" onKeyDown={onEnter(login)} onChange={(event) => updateField("password", event.target.value)} />
                </Field>
                <div className="auth-modern-action">
                  <Button className="auth-modern-primary" disabled={isBusy} onClick={login}>
                    <span>{isBusy ? "Signing in..." : "Sign in"}</span>
                    <i className="pi pi-arrow-right" aria-hidden="true" />
                  </Button>
                </div>
                <div className="auth-modern-divider">
                  <a href="/signup" onClick={(event) => { event.preventDefault(); navigate("/signup"); }}>Create an account</a>
                </div>
              </Card>
            ) : null}

            {currentStage === STAGE_EMAIL_VERIFICATION ? (
              <Card className="loginCard auth-modern-card auth-modern-card-narrow">
                <h2 className="auth-modern-card-title">Email Verification</h2>
                <p className="auth-modern-card-copy">Enter the verification code sent to your email address.</p>
                <Field label="Verification Code" required className="auth-modern-field" error={fieldErrors.verificationCode}>
                  <input type="text" className="form-control" placeholder="Verification Code" value={verificationCode} autoComplete="one-time-code" inputMode="numeric" onKeyDown={onEnter(verify)} onChange={(event) => updateVerificationCode(event.target.value)} />
                </Field>
                <div className="auth-modern-actions-row">
                  <Button label={isBusy ? "Submitting..." : "Submit"} className="auth-modern-primary" disabled={isBusy} onClick={verify} />
                  <Button label="Cancel" className="auth-modern-secondary" disabled={isBusy} onClick={() => setCurrentStage(STAGE_LOGIN)} />
                </div>
              </Card>
            ) : null}

            {currentStage === STAGE_MIGRATED_EMAIL_VERIFICATION ? (
              <Card className="loginCard auth-modern-card auth-modern-card-narrow">
                <h2 className="auth-modern-card-title">Verify your email</h2>
                <p className="auth-modern-card-copy">
                  You're about to log in to the new Trustd.ai Host Portal.
                  <br />
                  We'll send a one-time verification code to your email address. Enter the code to verify your email and continue.
                </p>
                <Field label="One-Time Verification Code" required className="auth-modern-field" error={fieldErrors.verificationCode}>
                  <input type="text" className="form-control" placeholder="Verification Code" value={verificationCode} autoComplete="one-time-code" inputMode="numeric" onKeyDown={onEnter(verifyMigratedEmail)} onChange={(event) => updateVerificationCode(event.target.value)} />
                </Field>
                <div className="auth-modern-actions-row">
                  <Button label={isBusy ? "Verifying..." : "Verify Email"} className="auth-modern-primary" disabled={isBusy} onClick={verifyMigratedEmail} />
                  <Button label="Cancel" className="auth-modern-secondary" disabled={isBusy} onClick={() => setCurrentStage(STAGE_LOGIN)} />
                </div>
              </Card>
            ) : null}

            {currentStage === STAGE_MIGRATED_SET_PASSWORD ? (
              <Card className="loginCard auth-modern-card auth-modern-card-narrow">
                <h2 className="auth-modern-card-title">Create your new password</h2>
                <p className="auth-modern-card-copy">Your email is verified. Set the password you'll use for all future Trustd.ai Host Portal sign-ins.</p>
                <Field label="New Password" required className="auth-modern-field" error={fieldErrors.newPassword1}>
                  <input type="password" className="form-control" placeholder="At least 8 characters" value={loginForm.newPassword1} autoComplete="new-password" onKeyDown={onEnter(completeMigratedAccount)} onChange={(event) => updateField("newPassword1", event.target.value)} />
                </Field>
                <Field label="Re-Enter New Password" required className="auth-modern-field" error={fieldErrors.newPassword2}>
                  <input type="password" className="form-control" placeholder="Re-enter new password" value={loginForm.newPassword2} autoComplete="new-password" onKeyDown={onEnter(completeMigratedAccount)} onChange={(event) => updateField("newPassword2", event.target.value)} />
                </Field>
                <div className="auth-modern-actions-row">
                  <Button label={isBusy ? "Activating..." : "Set Password & Continue"} className="auth-modern-primary" disabled={isBusy} onClick={completeMigratedAccount} />
                  <Button label="Cancel" className="auth-modern-secondary" disabled={isBusy} onClick={() => setCurrentStage(STAGE_LOGIN)} />
                </div>
              </Card>
            ) : null}

            {currentStage === STAGE_FORGOT_PASSWORD ? (
              <Card className="loginCard auth-modern-card auth-modern-card-narrow">
                <h2 className="auth-modern-card-title">Forgot Password</h2>
                <p className="auth-modern-card-copy">We will send a reset code to your email address.</p>
                <Field label="Email address" required className="auth-modern-field" error={fieldErrors.username}>
                  <input type="email" className="form-control" placeholder="you@example.com" value={loginForm.username} autoComplete="username" autoCapitalize="none" spellCheck="false" onKeyDown={onEnter(forgotPasswordReset)} onChange={(event) => updateField("username", event.target.value)} />
                </Field>
                <div className="auth-modern-actions-row">
                  <Button label={isBusy ? "Sending..." : "Reset Password"} className="auth-modern-primary" disabled={isBusy} onClick={forgotPasswordReset} />
                  <Button label="Cancel" className="auth-modern-secondary" disabled={isBusy} onClick={() => setCurrentStage(STAGE_LOGIN)} />
                </div>
              </Card>
            ) : null}

            {currentStage === STAGE_FORGOT_PASSWORD_CODE ? (
              <Card className="loginCard auth-modern-card auth-modern-card-narrow">
                <h2 className="auth-modern-card-title">Reset Password</h2>
                <p className="auth-modern-card-copy">Please check your email for the password reset code.</p>
                <Field label="Password Reset Code" required className="auth-modern-field" error={fieldErrors.code}>
                  <input type="text" className="form-control" placeholder="code" value={loginForm.code} autoComplete="one-time-code" inputMode="numeric" onKeyDown={onEnter(forgotPasswordCode)} onChange={(event) => updateField("code", event.target.value)} />
                </Field>
                <Field label="New Password" required className="auth-modern-field" error={fieldErrors.newPassword1}>
                  <input type="password" className="form-control" placeholder="New Password" value={loginForm.newPassword1} autoComplete="new-password" onKeyDown={onEnter(forgotPasswordCode)} onChange={(event) => updateField("newPassword1", event.target.value)} />
                </Field>
                <Field label="Re-Enter New Password" required className="auth-modern-field" error={fieldErrors.newPassword2}>
                  <input type="password" className="form-control" placeholder="New Password" value={loginForm.newPassword2} autoComplete="new-password" onKeyDown={onEnter(forgotPasswordCode)} onChange={(event) => updateField("newPassword2", event.target.value)} />
                </Field>
                <div className="auth-modern-actions-row">
                  <Button label={isBusy ? "Updating..." : "Change Password"} className="auth-modern-primary" disabled={isBusy} onClick={forgotPasswordCode} />
                  <Button label="Cancel" className="auth-modern-secondary" disabled={isBusy} onClick={() => setCurrentStage(STAGE_LOGIN)} />
                </div>
              </Card>
            ) : null}

            {currentStage === STAGE_COMPLETE_NEW_PASSWORD_REQUIRED ? (
              <ChangePassword passwordUser={passwordUser} onCancel={() => setCurrentStage(STAGE_LOGIN)} />
            ) : null}
        </div>
        <div className="auth-modern-signals" aria-label="Trustd.ai capabilities">
          <span><i className="pi pi-verified" aria-hidden="true" />ID Verification</span>
          <span><i className="pi pi-shield" aria-hidden="true" />Instant Background Checks</span>
        </div>
      </main>
      <footer className="auth-modern-footer">© 2024 Trustd.ai · All rights reserved</footer>
    </div>
  );
}
